/**
 * 目的：跨四个项目，React、Taro、Vue3
 * TODO：现阶段因为业务少，未抽成独立SDK，未来有时间、需求可以将所有登录相关业务抽离，便于管理
 * @see https://kb.cvte.com/pages/viewpage.action?pageId=349997540
 */

// 按道理下面这块也要抽象的，但是结合实际，直接放进来好了
import Enc2HEX from 'crypto-js/enc-hex';
import SHA256 from 'crypto-js/sha256';
import { ResLoginSuccess, Store } from './type';

// 密码登录状态
export enum LoginPasswordResStatus {
  /** 成功 */
  SUCCESS,
  /** 需要图形验证码 */
  NEED_GRAPHIC_CAPTCHA,
  /** 图形验证码错误 */
  GRAPHIC_CAPTCHA_ERROR,
  /** 账号或者密码错误 */
  ERROR,
  /** 手机号未注册 */
  MOBILE_NOT_EXISTS,
  /** 未知错误 */
  UNKNOWN
}

interface Params {
  API: {
    getSessionId: () => Promise<string>;
    getGraphicCaptchaUrl: () => string | Promise<string>;
    loginByPassword: ({ username, password, sessionId, graphicCaptcha }: any) => Promise<ResLoginSuccess>;
  };
  formStore: Store<{
    agreement: boolean;
    mobile: string;
    password: string;
    graphicCaptcha: string;
  }>;
  stateStore: Store<{
    loginLoading: boolean;
    graphicCaptchaUrl: string;
    graphicCaptchaError: string;
    passwordError: string;
  }>;
}

export class PasswordLogin {
  private _API;
  public formStore;
  public stateStore;

  constructor(params: Params) {
    this._API = params.API;
    this.formStore = params.formStore;
    this.stateStore = params.stateStore;
  }

  // 加密
  _encryptPassword(passowrd: string) {
    return Enc2HEX.stringify(SHA256(passowrd.trim()));
  }

  // 清除校验错误信息
  _resetValidateError() {
    this.stateStore.set({
      passwordError: '',
      graphicCaptchaError: ''
    });
  }

  // 执行手机号、密码获取登录信息
  fetchPasswordLogin = async () => {
    this.stateStore.set({ loginLoading: true });

    // 清除校验错误信息
    this._resetValidateError();

    try {
      const sessionId = await this._API.getSessionId();
      const { mobile, password, graphicCaptcha } = this.formStore.value;
      const res = await this._API.loginByPassword({
        username: mobile,
        password: this._encryptPassword(password),
        sessionId,
        graphicCaptcha: graphicCaptcha
      });
      return {
        data: res,
        status: LoginPasswordResStatus.SUCCESS
      };
    } catch (err: any) {
      const errorCode = err?.response?.data?.code;
      if (errorCode === 4001003) {
        // 需要图形验证码
        this.refrashGraphicCaptchaUrl(true);
        return { status: LoginPasswordResStatus.NEED_GRAPHIC_CAPTCHA };
      } else if (errorCode === 4001004) {
        // 图形验证码错误
        this.stateStore.set({ graphicCaptchaError: '图形验证码错误' });
        this.refrashGraphicCaptchaUrl(true);
        return { status: LoginPasswordResStatus.GRAPHIC_CAPTCHA_ERROR };
      } else if (errorCode === 4001000 || errorCode === 4001007) {
        // 账号或者密码错误
        this.stateStore.set({ passwordError: '账号不存在或密码错误，请重新输入' });
        this.refrashGraphicCaptchaUrl(false);
        return { status: LoginPasswordResStatus.ERROR };
      } else {
        this.refrashGraphicCaptchaUrl(false);
        this.stateStore.set({ passwordError: '未知错误' });
        return { status: LoginPasswordResStatus.UNKNOWN };
      }
    } finally {
      this.stateStore.set({ loginLoading: false });
    }
  };

  // 刷新图形验证码
  refrashGraphicCaptchaUrl = async (force = true) => {
    try {
      if (!force && !this.stateStore.value.graphicCaptchaUrl) return;
      const url = await this._API.getGraphicCaptchaUrl();
      this.stateStore.set({ graphicCaptchaUrl: url });
    } catch (e) {
      console.log(e);
    }
  };
}

export default PasswordLogin;
